import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CookieConsent } from "@/components/analytics/CookieConsent";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";

// Critical path — eager load
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

// Lazy load everything else
const Assessment = lazy(() => import("./pages/Assessment"));
const Quiz = lazy(() => import("./pages/Quiz"));
const Chat = lazy(() => import("./pages/Chat"));
const CounselBot = lazy(() => import("./pages/CounselBot"));
const Courses = lazy(() => import("./pages/Courses"));
const Journal = lazy(() => import("./pages/Journal"));
const PortfolioPricing = lazy(() => import("./pages/PortfolioPricing"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const AttachmentCurriculum = lazy(() => import("./pages/AttachmentCurriculum"));
const LeaderMaintenance = lazy(() => import("./pages/LeaderMaintenance"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const Crisis = lazy(() => import("./pages/Crisis"));
const Community = lazy(() => import("./pages/Community"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Certification = lazy(() => import("./pages/Certification"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const LessonViewer = lazy(() => import("./pages/LessonViewer"));
const Achievements = lazy(() => import("./pages/Achievements"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Admin = lazy(() => import("./pages/Admin"));
const Forum = lazy(() => import("./pages/Forum"));
const Settings = lazy(() => import("./pages/Settings"));
const BereanDashboard = lazy(() => import("./pages/BereanDashboard"));
const Advocates = lazy(() => import("./pages/Advocates"));
const LeaderTraining = lazy(() => import("./pages/LeaderTraining"));
const VerifyCertificate = lazy(() => import("./pages/VerifyCertificate"));
const Library = lazy(() => import("./pages/Library"));
const LibraryResource = lazy(() => import("./pages/LibraryResource"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PreLaunchChecklist = lazy(() => import("./pages/PreLaunchChecklist"));
const SafeSoulRegistry = lazy(() => import("./pages/SafeSoulRegistry"));
const ChurchApplication = lazy(() => import("./pages/ChurchApplication"));
const Waitlist = lazy(() => import("./pages/Waitlist"));
const Install = lazy(() => import("./pages/Install"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const LiveCertification = lazy(() => import("./pages/LiveCertification"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/pricing" element={<PortfolioPricing />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/crisis" element={<Crisis />} />
                <Route path="/safe-soul-registry" element={<SafeSoulRegistry />} />
                <Route path="/church-application" element={<ProtectedRoute><ChurchApplication /></ProtectedRoute>} />
                <Route path="/waitlist" element={<Waitlist />} />
                <Route path="/install" element={<Install />} />
                <Route path="/testimonials" element={<Testimonials />} />
                <Route path="/verify-certificate" element={<VerifyCertificate />} />
                
                {/* SoulSafe Routes */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/soulsafe" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/assessment" element={<ProtectedRoute><Assessment /></ProtectedRoute>} />
                <Route path="/quiz" element={<ProtectedRoute><Quiz /></ProtectedRoute>} />
                <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
                <Route path="/counsel-bot" element={<ProtectedRoute><CounselBot /></ProtectedRoute>} />
                <Route path="/courses" element={<ProtectedRoute><Courses /></ProtectedRoute>} />
                <Route path="/courses/:courseId" element={<ProtectedRoute><CourseDetail /></ProtectedRoute>} />
                <Route path="/courses/:courseId/lesson/:lessonId" element={<ProtectedRoute><LessonViewer /></ProtectedRoute>} />
                <Route path="/journal" element={<ProtectedRoute><Journal /></ProtectedRoute>} />
                <Route path="/subscription-success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
                <Route path="/attachment" element={<ProtectedRoute><AttachmentCurriculum /></ProtectedRoute>} />
                <Route path="/leader-maintenance" element={<ProtectedRoute><LeaderMaintenance /></ProtectedRoute>} />
                <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
                <Route path="/certification" element={<ProtectedRoute><Certification /></ProtectedRoute>} />
                <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
                <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
                <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
                <Route path="/admin/pre-launch" element={<AdminRoute><PreLaunchChecklist /></AdminRoute>} />
                <Route path="/forum" element={<ProtectedRoute><Forum /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/advocates" element={<ProtectedRoute><Advocates /></ProtectedRoute>} />
                <Route path="/leader-training" element={<ProtectedRoute><LeaderTraining /></ProtectedRoute>} />
                <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
                <Route path="/library/:resourceId" element={<ProtectedRoute><LibraryResource /></ProtectedRoute>} />
                <Route path="/live-certification" element={<ProtectedRoute><LiveCertification /></ProtectedRoute>} />
                {/* Berean Mode Routes */}
                <Route path="/berean" element={<ProtectedRoute><BereanDashboard /></ProtectedRoute>} />
                <Route path="/berean/dashboard" element={<ProtectedRoute><BereanDashboard /></ProtectedRoute>} />
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <CookieConsent />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
